import React, { useEffect, useMemo } from 'react';
import Mapbox, { useMap } from 'react-map-gl';
import * as MapboxJs from 'mapbox-gl';
import { Box, Chip } from '@material-ui/core';
import MapSpots from './MapSpots';
import { MAPBOX_DARK_MAP_STYLE, MAPBOX_LIGHT_MAP_STYLE, MAPBOX_TOKEN } from '../../config/map';
import { useTheme } from '../contexts/theme';
import { getBounds, buildSweepSteps, getOverviewFrame, MAX_SWEEP_ZOOM } from '../utils/map';
import { useTilesCache } from '../hooks/useTilesCache';
import Loading from './Loading';
import { useSession } from '../contexts/session';
import AccuracyInfo from './AccuracyInfo';

const Map = ({ spots, styleOverride, loaded, userLocation, prefetch, onPrefetchProgress }) => {
  const { offline, selectedExperience } = useSession();
  const { theme } = useTheme();
  const tilesCache = useTilesCache();

  const bounds = useMemo(() => getBounds(spots.map((spot) => spot.position)), [spots]);

  const mapStyle = useMemo(
    () => styleOverride || (theme === 'day' ? MAPBOX_LIGHT_MAP_STYLE : MAPBOX_DARK_MAP_STYLE),
    [theme, styleOverride]
  );

  return (
    <Box width="100%" height="100%" position="relative">
      {tilesCache ? (
        <Mapbox
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={mapStyle}
          attributionControl={false}
          style={
            // During prefetch the map is hidden; a fixed, larger canvas lets each
            // sweep frame cover a 2x2 tile block (vs ~1 tile at phone width), cutting
            // the number of camera steps ~4x. Normal display fills its container.
            prefetch ? { height: 1024, width: 1024 } : { height: '100%', width: '100%' }
          }
          initialViewState={{
            bounds,
            fitBoundsOptions: {
              padding: {
                top: 20,
                left: 20,
                right: 20,
                bottom: 20,
              },
            },
          }}
          onLoad={prefetch ? undefined : loaded && loaded}
          interactive
          refreshExpiredTiles={false}
          fog={undefined}
          styleDiffing={false}
        >
          {prefetch && <TilePrefetcher bounds={bounds} onDone={loaded} onProgress={onPrefetchProgress} />}
          {offline && !prefetch && <OfflineCameraLock />}
          {!!userLocation && <UserLocation controlCamera={!offline} />}
          {spots?.length > 0 && <MapSpots />}
          {selectedExperience && (
            <Box position="absolute" top={0} left={0} p={1}>
              <Chip
                label={selectedExperience.label}
                variant="filled"
                size="small"
                sx={{
                  backgroundColor: '#6a6f7a',
                  color: '#fff',
                  fontWeight: 700,
                }}
              />
            </Box>
          )}
          <Box position="absolute" bottom={0} right={0}>
            <AccuracyInfo />
          </Box>
        </Mapbox>
      ) : (
        <Loading />
      )}
    </Box>
  );
};

const IDLE_TIMEOUT_MS = 8000;

// Fixed screen margin (px) added around the offline pan limit so zooming back
// out preserves the initial fitBounds padding. Kept in pixels (not a %) so it
// looks the same regardless of the experience's real-world size.
const MAX_BOUNDS_PADDING_PX = 20;

// Resolves when the map settles after a camera move (all tiles loaded/rendered),
// or after a timeout so a single hung/errored tile can't stall the whole sweep.
const waitForIdle = (map, timeout = IDLE_TIMEOUT_MS) =>
  new Promise((resolve) => {
    let timer;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      map.off('idle', finish);
      resolve();
    };
    timer = setTimeout(finish, timeout);
    map.on('idle', finish);
  });

// Drives the (hidden) map across the whole experience bbox at every zoom up to
// the source max, so mapbox-gl fetches every covering tile; the service worker
// persists them. Calls `onDone` once the sweep completes (or is unmounted).
function TilePrefetcher({ bounds, onDone, onProgress }) {
  const { current: map } = useMap();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!map.isStyleLoaded()) {
        await new Promise((resolve) => {
          map.once('load', resolve);
        });
      }
      if (cancelled) return;

      const source = map.getSource('composite');
      const maxZoom = Math.min(MAX_SWEEP_ZOOM, source?.maxzoom ?? MAX_SWEEP_ZOOM);

      // Cache the overview frame the display map will actually show (the fitBounds
      // rectangle for this screen's aspect ratio), not just the tight spot bounds —
      // otherwise the empty margin around the spots is blank offline. The display
      // map's container is at most this large, so the window-sized frame covers it.
      const { frame, zoom: frameZoom } = getOverviewFrame(bounds, window.innerWidth, window.innerHeight);
      // one extra level out for safety, but never above the source max
      const minZoom = Math.min(maxZoom, Math.max(0, Math.floor(frameZoom) - 1));
      const container = map.getContainer();
      const steps = buildSweepSteps(frame, minZoom, maxZoom, {
        width: container.clientWidth,
        height: container.clientHeight,
      });

      onProgress?.(0, steps.length);
      for (let i = 0; i < steps.length; i += 1) {
        if (cancelled) return;
        map.jumpTo(steps[i]);
        // eslint-disable-next-line no-await-in-loop
        await waitForIdle(map);
        onProgress?.(i + 1, steps.length);
      }

      if (!cancelled) onDone?.();
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [map]);

  return null;
}

// Offline, lock the camera to the fitted overview once it settles: the user
// sees all spots, can zoom in and pan within that frame, but can't zoom out
// past it or pan into the uncached void. Applied after the initial fitBounds
// (via the `idle` event) so it captures the real overview, avoiding the
// maxBounds-vs-aspect-ratio conflict that a static bbox would cause.
function OfflineCameraLock() {
  const { current: map } = useMap();

  useEffect(() => {
    if (!map) return undefined;
    // setMinZoom/setMaxBounds are NOT proxied by react-map-gl's MapRef (they're
    // in its skipMethods list), so we must call them on the raw mapbox instance.
    const raw = map.getMap();
    let locked = false;
    let timer;
    const lock = () => {
      if (locked) return;
      locked = true;
      clearTimeout(timer);
      raw.off('idle', lock);
      // minZoom = the padded overview zoom is the real floor. maxBounds is
      // expanded by a fixed *pixel* margin so mapbox's own "fit maxBounds
      // edge-to-edge" minimum zoom stays below that floor — otherwise zooming
      // back out clamps to the edge and eats the initial padding. A pixel margin
      // (vs a %) keeps the behaviour consistent across 1km and 20km areas, like
      // the fitBounds padding it mirrors.
      raw.setMinZoom(raw.getZoom());
      const b = raw.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const container = raw.getContainer();
      const degPerPxLng = (ne.lng - sw.lng) / (container.clientWidth || 1);
      const degPerPxLat = (ne.lat - sw.lat) / (container.clientHeight || 1);
      const padLng = degPerPxLng * MAX_BOUNDS_PADDING_PX;
      const padLat = degPerPxLat * MAX_BOUNDS_PADDING_PX;
      raw.setMaxBounds([
        [sw.lng - padLng, sw.lat - padLat],
        [ne.lng + padLng, ne.lat + padLat],
      ]);
    };
    // Offline the cached tiles load instantly, so the map may already be idle
    // before we subscribe (no further 'idle' would fire). Cover every case:
    // lock now if it's already settled, on the next idle otherwise, and a
    // timeout as a final fallback. The initial fitBounds is applied at map
    // construction, so the current camera is the correct overview either way.
    raw.on('idle', lock);
    if (raw.loaded()) lock();
    timer = setTimeout(lock, 3000);
    return () => {
      clearTimeout(timer);
      raw.off('idle', lock);
    };
  }, [map]);

  return null;
}

function UserLocation({ controlCamera }) {
  const { current: map } = useMap();

  useEffect(() => {
    const control = new MapboxJs.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
      showUserLocation: true,
    });

    if (!controlCamera) {
      // hacky workaround for the fact that mapbox doesn't let you disable camera auto-tracking
      // eslint-disable-next-line
      control._updateCamera = () => {};
    }

    map.addControl(control);
    map.on('load', () => control.trigger());

    return () => map.removeControl(control);
  }, [map]);

  return null;
}

export default Map;
